import Vue from "vue";
import Router from "vue-router";
import store from "@/store";
import api from "@/api";
import { decodeHTML } from "entities";
import { authUserAuthorised, authUserIsCurator } from "@/utils/auth"; // authDomainAuthorised
import { updateCpdPoints } from "@/utils/toolbox";
import { adminRoutes } from "@/router/adminRoutes";
// import { curationRoutes } from "@/router/curationRoutes";
import { forumRoutes } from "@/router/forumRoutes";
import { notificationRoutes } from "@/router/notificationRoutes";
import { userRoutes } from "@/router/userRoutes";

import ErrorForbidden from "@/views/Forbidden/ErrorForbidden.vue";
import NotFound from "@/views/NotFound/NotFound.vue";

Vue.use(Router);

// Set up the available routes
// We only include routes which are ready for production.
let routes = [
  ...userRoutes,
  {
    path: "/forbidden",
    name: "forbidden",
    component: ErrorForbidden,
    meta: {
      role: false,
      allowWhenForcePasswordChange: true,
    },
  },
  {
    path: "*",
    name: "not-found",
    component: NotFound,
    meta: {
      role: false,
      allowWhenForcePasswordChange: true,
    },
  },
  ...forumRoutes,
  ...notificationRoutes,
  ...adminRoutes,
];
// if (process.env.NODE_ENV === "development") {
//   routes = [...routes, ...curationRoutes];
// }

const router = new Router({
  mode: "history",
  base: process.env.BASE_URL,
  routes,
});

router.beforeEach(async (to, from, next) => {
  // Check whether the site config and currently logged-in user have been fetched.
  if (
    !store.state.config.fetched ||
    !store.state.user.fetched ||
    !store.state.pages.fetched ||
    !store.state.pdlTemplate.fetched
  ) {
    const [config, pages] = await api.getMultiple(["/api2/config", "/api2/page/navigation"]);
    store.commit("setVersion", config.data.version);
    store.commit("setConfig", config.data.config);
    store.commit("setUser", config.data.user);
    store.commit("setPages", pages.data);
    store.commit("setPdlTemplate", config.data.pdlTemplate);

    // Set the HTML editor config to use based on the user's role.
    if (authUserIsCurator()) {
      store.commit("setCuratorEditorConfig");
    }

    // Once we have all the things, update the CPD points
    await updateCpdPoints(api, store);
  }

  // Check whether the current domain supports the route.
  // Not currently used. Leaving for reference in case we need to reinstate.
  // if (!authDomainAuthorised(to.meta.sites)) {
  //   next("/forbidden");
  //   return;
  // }

  // Check that the user has correct role to access the route.
  // Non logged in users are redirected to the login page.
  if (!authUserAuthorised(to.meta.role)) {
    if (store.state.user.logged_in) {
      // Logged in users have no permission to access the page - show forbidden.
      next("/forbidden");
    } else {
      // Non logged in users are redirected to the login page.
      api.redirectToLogin();
    }
    return;
  }

  // If the user is logged in, check whether they need to be on another page ...
  if (store.state.user.logged_in) {
    // Check whether the user must change their password, i.e. they have the force_password_change flag set.
    if (store.state.user.force_password_change) {
      if (!to.meta.allowWhenForcePasswordChange) {
        return next({ name: "changePassword" });
      }
    } else if (store.state.user.next) {
      // Check whether the user's next flag is set and redirect as needed
      // Note this is only done if the force_password_change flag is not set.
      const nextpath = store.state.user.next; // Temporarily store this outside the store, since store will be cleared
      store.commit("clearUserNext"); // Unset next to prevent further redirections after the route navigates.
      await api.patch("/api2/user/clear-next"); // TODO: error handling. Clear next flag on the user via the back-end.
      // The window.location.href approach is hacky. I would prefer to use `return next(nextpath)` but it throws a
      // recursion error. In fairness, it may also not properly handle URLs which are outside of the Vue app (e.g. ones
      // in the legacy site).
      window.location.href = nextpath;
      // TODO: revert the set password page so that it no longer clears the next flag
    }
  }

  // If the user is trying to access a forum page, check whether they are allowed to.
  if (to.meta.forum && !store.state.config.forum_on) {
    next("/forbidden");
    return;
  }

  // If the user is trying to access a payment page, check whether they are allowed to.
  if (to.meta.payment && !store.state.config.group_payment_on) {
    next("/forbidden");
    return;
  }

  // Set the page title
  if (to.meta.siteTitle) {
    document.title = decodeHTML(`${store.state.config.site_title} - ${to.meta.siteTitle}`);
  } else {
    document.title = decodeHTML(store.state.config.site_title);
  }

  // Set the page banner.
  if (to.meta.banner) {
    store.commit("setBanner", to.meta.banner);
  } else {
    store.commit("clearBanner");
  }

  // Clear the page background.
  // Backgrounds are set by code in the current view, after some database fetch (e.g. for collections or content).
  store.commit("clearBackground");

  // Set the page title.
  if (to.meta.title) {
    store.commit("setTitle", to.meta.title);
  } else {
    store.commit("clearTitle");
  }

  // Set the page breadcrumbs.
  if (to.meta.breadcrumbs) {
    store.commit("setBreadcrumbs", to.meta.breadcrumbs);
  } else {
    store.commit("clearBreadcrumbs");
  }

  // Resolve the beforeEach routing hook.
  next();
});

export default router;
